import { Injectable } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { generateClient, CONNECTION_STATE_CHANGE, ConnectionState } from 'aws-amplify/api';
import { Hub } from 'aws-amplify/utils';
import { decodeJWT } from '@aws-amplify/core';
import { environment } from 'src/environments/environment';
import { UserService } from '../user/user.service';

const subscribeDoc = /* GraphQL */ `
  subscription Subscribe($name: String!) {
    subscribe(name: $name) {
      data
      name
    }
  }
`;

const publishDoc = /* GraphQL */ `
  mutation Publish($data: AWSJSON!, $name: String!) {
    publish(data: $data, name: $name) {
      data
      name
    }
  }
`;

@Injectable({
  providedIn: 'root'
})
export class AmplifyService {
  public connectionState: string;
  private connectionCallback: CallableFunction;
  private client: ReturnType<typeof generateClient>;

  constructor(
    private user: UserService,
  ) {}

  public async configure() {
    Amplify.configure({
      API: {
        GraphQL: {
          endpoint: environment.aws.endpoint,
          region: environment.aws.region,
          defaultAuthMode: 'oidc',
        },
      },
    }, {
      API: {
        GraphQL: {
          headers: async () => {
            try {
              await this.user.isReady();
              const tokenResult = await this.user.user.getIdTokenResult();
              return {
                Authorization: tokenResult.token,
              };
            } catch {
              return {};
            }
          },
        },
      },
      Auth: {
        tokenProvider: {
          getTokens: async ({ forceRefresh } = {}) => {
            try {
              await this.user.isReady();
              const tokenResult = await this.user.user.getIdTokenResult(forceRefresh);
              const jwt = decodeJWT(tokenResult.token);
              return {
                accessToken: jwt,
                idToken: jwt,
              };
            } catch {
              return null;
            }
          },
        },
      },
    });
    this.client = generateClient();
    Hub.listen('api', ({ payload }) => {
      if (payload.event === CONNECTION_STATE_CHANGE) {
        const data = payload.data as { connectionState: string };
        this.connectionState = data.connectionState;
        if (this.connectionCallback) {
          this.connectionCallback(data.connectionState);
        }
      }
    });
  }

  public setConnectionCallback(callback?: CallableFunction) {
    this.connectionCallback = callback;
  }

  public async publish(name, data, waitReady = true) {
    if (waitReady) {
      await this.isReady();
    }

    return await this.client.graphql({
      query: publishDoc,
      variables: { name, data },
    });
  }

  public async subscribe(name, next, error) {
    const graphql = this.client.graphql({
      query: subscribeDoc,
      variables: { name },
    });

    return (graphql as any).subscribe({
      next: ({ data }) => {
        next(data.subscribe);
      },
      error: error || console.log,
    });
  }

  public async isReady(): Promise<void> {
    if (this.connectionState === ConnectionState.Connected) {
      return;
    }

    return new Promise((resolve) => {
      let retry = 0;
      const interval = setInterval(async () => {
        if (this.connectionState === ConnectionState.Connected || retry > 30) {
          clearInterval(interval);
          resolve();
        }
        retry++;
      }, 1000);
    });
  }
}
